'use client';
import { cn } from '@dolcevia/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@dolcevia/ui/components/ui/popover';
import { groupBy } from 'lodash';
import { Check, ChevronDown, ListFilter, XIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { useQueryState } from 'nuqs';
import { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';

type FilterProps = {
  setCategories: ReturnType<typeof useQueryState<string[]>>[1];
  items: Array<{
    value: string;
    label: string;
  }>;
  className?: string;
  defaultValue?: string[];
  selectedCategories?: string[];
  inlineFilter?: boolean;
};
export const Filter = ({
  setCategories,
  items,
  className,
  defaultValue,
  selectedCategories,
  inlineFilter = true,
}: FilterProps) => {
  const [open, setOpen] = useState(false);
  const t = useTranslations('Home.Filter');

  const handleRemoveStatus = async (status: string) => {
    if (!selectedCategories) return;
    setCategories(selectedCategories.filter((selected) => selected !== status));
  };
  return (
    <>
      <ToggleGroup
        onValueChange={setCategories}
        defaultValue={defaultValue}
        type="multiple"
        className={cn(
          'gap-4 text-slate-500',
          inlineFilter ? 'hidden md:block' : 'hidden',
          className,
        )}
      >
        {items.map((item) => (
          <ToggleGroupItem
            key={item.value}
            value={item.value}
            variant="outline"
            className="data-[state=on]:text-deep-blue-100 data-[state=on]:underline"
          >
            {item.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <div
        className={cn(
          'flex gap-4 flex-wrap w-full',
          inlineFilter ? 'md:hidden' : '',
        )}
      >
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="justify-start gap-3 w-min items-center col-start-[none]"
            >
              <>
                <ListFilter />
                {t('Filtres')}
                <ChevronDown />
              </>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0" align="start">
            <FilterList
              setOpen={setOpen}
              selectedCategories={selectedCategories}
              setCategory={setCategories}
              items={items}
            />
          </PopoverContent>
        </Popover>
        {selectedCategories?.map((status) => (
          <Badge key={status} variant="default" className="gap-3 w-max">
            {items.find((filter) => filter.value === status)?.label}
            <Button
              size="icon"
              variant="outline"
              className="h-5 w-5"
              onClick={async () => await handleRemoveStatus(status)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </Badge>
        ))}
      </div>
    </>
  );
};

function FilterList({
  setOpen,
  selectedCategories = [],
  setCategory,
  items,
}: {
  items: Array<{
    value: string;
    label: string;
    group?: string;
  }>;
  selectedCategories?: string[];
  setOpen: (open: boolean) => void;
  setCategory: ReturnType<typeof useQueryState<string[]>>[1];
}) {
  const t = useTranslations('Home.Filter');
  const itemsByGroup = groupBy(items, 'group');
  return (
    <Command>
      <CommandInput placeholder={t('Filtrer par')} />
      <CommandList>
        {Object.entries(itemsByGroup).map(([group, items]) => (
          <CommandGroup
            key={group}
            heading={group === 'undefined' ? '' : group}
          >
            {items.map((item) => (
              <CommandItem
                key={item.value}
                value={item.value}
                onSelect={(value) => {
                  setCategory((prev) => {
                    if (prev?.includes(value)) {
                      return prev.filter((v) => v !== value);
                    }
                    return [...(prev ?? []), value];
                  });
                  setOpen(false);
                }}
              >
                {item.label}
                <Check
                  className={cn(
                    'ml-auto',
                    selectedCategories?.includes(item.value)
                      ? 'opacity-100'
                      : 'opacity-0',
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}
